import type { PdpAktion, PdpDetails, PdpEntscheidungsAngaben } from '@awf/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { useAngemeldeterNutzer } from '../auth/auth-context.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { EmptyState, ErrorState, LoadingState } from '../ui/states.js';
import { AbcLegende, BefundBericht, BlockKacheln } from './befund-bloecke.js';
import stile from './entscheidung-page.module.css';
import { istFremd, NUR_ANSICHT_TEXT } from './einsicht.js';
import { EntscheidungSpalte } from './entscheidung-spalte.js';
import { KeywordHinweisKasten, OHNE_KEYWORD_SPERRE } from './keyword-hinweis.js';
import { type BlockSchluessel, zahlenAusDetails } from './pdp-bloecke.js';
import { kurzzeile } from './ranking.js';
import type { AuftragsQuelle } from './runs-source.js';
import { useAuftrag } from './use-runs.js';

type DetailZustand =
  | { art: 'laedt' }
  | { art: 'daten'; details: PdpDetails }
  | { art: 'fehler'; text: string; endgueltig: boolean };

/**
 * Entscheidungsseite EINER Seite einer Ratgeber-Analyse (Spec 2026-10-13-ratgeber-analyse-frontend,
 * 6.2). Aufbau wie die PDP-Entscheidungsseite: Kopf und Kacheln aus Firestore, Details ueber die
 * Leseroute, rechts die typneutrale Spalte. Entscheiden haengt NICHT an den Details.
 */
export function AnalyseSeitePage({
  quelle,
  holen,
  entscheide,
}: {
  quelle: AuftragsQuelle;
  holen: (runId: string, auditId: string) => Promise<PdpDetails>;
  entscheide: (
    runId: string,
    auditId: string,
    aktion: PdpAktion,
    angaben?: PdpEntscheidungsAngaben,
  ) => Promise<void>;
}) {
  const { runId = '', auditId = '' } = useParams();
  const zustand = useAuftrag(quelle, runId);
  const uid = useAngemeldeterNutzer()?.uid ?? null;
  const auftrag = zustand.art === 'daten' ? zustand.auftrag : null;
  const seite =
    auftrag?.typ === 'analyse'
      ? auftrag.analyseBefund?.seiten.find((s) => s.auditId === auditId)
      : undefined;
  const bekannt = seite !== undefined;
  const [details, setDetails] = useState<DetailZustand>({ art: 'laedt' });
  const [versuch, setVersuch] = useState(0);

  // Haengt an `bekannt` statt an der Seite: jeder Snapshot liefert ein neues Objekt, die Details
  // sollen aber nur einmal (und auf "Erneut versuchen") geladen werden.
  useEffect(() => {
    if (!bekannt) return;
    let aktiv = true;
    setDetails({ art: 'laedt' });
    holen(runId, auditId).then(
      (d) => {
        if (aktiv) setDetails({ art: 'daten', details: d });
      },
      (f: unknown) => {
        if (!aktiv) return;
        const endgueltig =
          f instanceof ApiError &&
          (f.code === 'ueberholt' || f.code === 'seite_unbekannt' || f.code === 'nicht_gefunden');
        setDetails({
          art: 'fehler',
          text: f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT,
          endgueltig,
        });
      },
    );
    return () => {
      aktiv = false;
    };
  }, [holen, runId, auditId, bekannt, versuch]);

  const entscheideHier = useCallback(
    (aktion: PdpAktion, angaben?: PdpEntscheidungsAngaben) =>
      entscheide(runId, auditId, aktion, angaben),
    [entscheide, runId, auditId],
  );

  const waehle = useCallback((s: BlockSchluessel) => {
    const element = document.getElementById(`block-${s}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start' });
    }
  }, []);

  if (zustand.art === 'laedt') return <LoadingState text="Auftrag wird geladen …" />;
  if (zustand.art === 'fehler')
    return <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />;
  if (!auftrag) {
    return (
      <EmptyState
        titel="Dieser Auftrag existiert nicht oder gehört zu einem anderen Konto."
        text="Prüfen Sie den Verweis, über den Sie hierher gekommen sind."
      >
        <Link className="eds-button eds-button--primary" to="/">
          Zur Übersicht
        </Link>
      </EmptyState>
    );
  }
  const zumAuftrag = `/auftraege/${encodeURIComponent(auftrag.runId)}`;
  if (auftrag.typ !== 'analyse') return <Navigate to={zumAuftrag} replace />;
  if (!seite) {
    return (
      <EmptyState
        titel="Diese Seite gehört nicht zu dieser Analyse."
        text="Prüfen Sie den Verweis, über den Sie hierher gekommen sind."
      >
        <Link className="eds-button eds-button--secondary" to={zumAuftrag}>
          Zum Auftrag
        </Link>
      </EmptyState>
    );
  }

  // Unlesbare Empfehlungen zaehlen nicht als "0 Empfehlungen": dann gilt der Stand aus Firestore.
  const zahlen =
    details.art === 'daten' && !details.details.empfehlungenUnlesbar
      ? zahlenAusDetails(details.details)
      : seite.bloecke;
  const kurz = details.art === 'daten' ? kurzzeile(details.details.ranking) : undefined;
  // Gesamtansicht der Rolle `admin`: Seite lesen ja, entscheiden nein (das Backend weist es ab).
  const fremd = istFremd(auftrag, uid);
  // Seit dem 15.10. bleiben die Daten einer aelteren Analyse erhalten: Sie bleibt einsehbar, mit
  // Weg zur neueren, in der entschieden wird (Entscheidung Martin 2026-10-16).
  const neuere = seite.stand === 'ueberholt' ? seite.ueberholtDurch : undefined;
  const ohneKeyword = seite.keywordHinweis === 'fehlt';
  const entscheidbar =
    !fremd && auftrag.status === 'wartet_auf_entscheidung' && seite.stand === 'offen';

  return (
    <div className={stile.seite}>
      <Seitenkopf
        titel={seite.h1 || seite.url}
        pfad={[
          { text: 'Übersicht', ziel: '/' },
          {
            text: fremd ? 'Fortschritt des Auftrags' : 'Fortschritt Ihres Auftrags',
            ziel: zumAuftrag,
          },
        ]}
        beschreibung={
          <>
            <a className={stile.adresse} href={seite.url} target="_blank" rel="noreferrer">
              {seite.url}
            </a>
            <span className={stile.grund}>
              {fremd
                ? `Ratgeber-Analyse: ${NUR_ANSICHT_TEXT}`
                : neuere
                  ? 'Ratgeber-Analyse: Diese Analyse ist nicht mehr aktuell. Sie können den Befund weiter ansehen.'
                  : ohneKeyword
                    ? 'Ratgeber-Analyse: Für diese Seite gibt es keine Empfehlungen. Sie können sie rechts verwerfen.'
                    : 'Ratgeber-Analyse: Prüfen Sie die Empfehlungen und entscheiden Sie rechts, ob diese Seite optimiert wird.'}
            </span>
          </>
        }
      />
      <div className={stile.arbeitsflaeche}>
        <div className={stile.inhalt}>
          {seite.stand === 'optimiert' && seite.kindRunId && (
            <p>
              <Link
                className="eds-button eds-button--secondary eds-button--sm"
                to={`/auftraege/${encodeURIComponent(seite.kindRunId)}`}
              >
                Zum Optimierungsauftrag
              </Link>
            </p>
          )}
          <div className={stile.grundKasten}>
            <p className={stile.grundTitel}>Optimierungsgrund</p>
            <p>
              {seite.grund || 'Optimierung empfohlen'}
              {kurz && <span className={stile.leise}> · {kurz}</span>}
            </p>
          </div>
          <KeywordHinweisKasten seite={seite} entscheidbar={entscheidbar} />
          {/* Ohne Fokus-Keyword gaebe es nur Nullen: der Kasten darueber sagt, warum. */}
          {!ohneKeyword && (
            <>
              <BlockKacheln zahlen={zahlen} onWaehle={waehle} />
              <AbcLegende />
            </>
          )}
          {details.art === 'laedt' && <LoadingState text="Analysedaten werden geladen …" />}
          {details.art === 'fehler' &&
            (details.endgueltig ? (
              <div className="eds-alert eds-alert--warning" role="status">
                <p>{details.text}</p>
              </div>
            ) : (
              <ErrorState text={details.text} onWiederholen={() => setVersuch((v) => v + 1)} />
            ))}
          {details.art === 'daten' && <BefundBericht details={details.details} />}
        </div>
        {/* key: Eingaben und Sperre gehoeren zu genau einer Seite. */}
        <EntscheidungSpalte
          key={`${auftrag.runId}:${seite.auditId}`}
          offen={entscheidbar}
          entscheidung={seite.entscheidung}
          seitenart="Ratgeberseite"
          entscheide={entscheideHier}
          nurVerwerfen={ohneKeyword ? OHNE_KEYWORD_SPERRE : undefined}
          weiter={
            neuere
              ? { text: 'Zur neueren Analyse', pfad: `/auftraege/${encodeURIComponent(neuere)}` }
              : undefined
          }
          geschlossenGrund={
            fremd
              ? NUR_ANSICHT_TEXT
              : neuere
                ? // Ohne Datum: `entschiedenAm` ist der Zeitpunkt des Ueberholens, nicht der neueren
                  // Analyse (Codex-Review PR #92). Den Weg dorthin zeigt der Knopf darunter.
                  'Diese Seite wurde in einer neueren Analyse erneut geprüft. Entscheiden Sie bitte dort.'
                : seite.stand === 'ueberholt'
                  ? 'Diese Seite wurde in einer neueren Analyse geprüft. Entscheiden Sie bitte dort.'
                  : auftrag.status !== 'wartet_auf_entscheidung'
                    ? 'Diese Analyse ist abgeschlossen. Über diese Seite wurde nicht entschieden.'
                    : undefined
          }
        />
      </div>
    </div>
  );
}
